import { filter, map, Observable } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { AgentsGQL, Maybe, Locale } from '@iss/libs/graphql';
import { FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import {
  Component,
  Inject,
  inject,
  Input,
  OnChanges,
  OnInit,
  Self,
  SimpleChanges,
} from '@angular/core';

import { Agent, Agents } from './agent-input.types';

import { TypeaheadSelectComponent } from '@iss/core/typeahead-select';
import { Control, NOOP_VALUE_ACCESSOR } from '@iss/core/forms';

@Component({
  selector: 'wo-agent-input',
  templateUrl: './agent-input.component.html',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    TypeaheadSelectComponent,
  ],
})
export class AgentInputComponent implements OnInit, OnChanges {
  private readonly query = inject(AgentsGQL);

  public loading$!: Observable<boolean>;
  public items$!: Observable<Agents>;

  public readonly stringify = (agents: Agents | null): (id: Agent['id'] | 'ALL') => string =>
    id => id === 'ALL'
      ? 'All'
      : agents?.find(agent => agent.id === id)?.organization.name || '';

  public readonly matcher = (agent: Agent): string => agent.organization.name;

  public readonly content = (id: Agent['id'] | 'ALL', agents: Agents | null): string | undefined =>
    id === 'ALL'
      ? 'All'
      : agents?.find(agent => agent.id === id)?.organization.name;

  public readonly identity = (agent: Agent): Agent['id'] => agent.id;

  public constructor(
    @Self() @Inject(NgControl) public readonly control: Control<FormControl<Agent['id'] | null>>,
  ) {
    this.control.valueAccessor = NOOP_VALUE_ACCESSOR;
  }

  @Input() public placeholder: Maybe<string>;
  @Input() public readonly: Maybe<boolean>;
  @Input() public loading: Maybe<boolean>;
  @Input({ required: true }) public locale!: Locale;

  public ngOnInit(): void {
    this.initQuery();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('locale' in changes) {
      this.initQuery();
    }
  }

  private initQuery(): void {
    const query$ = this.query.watch({
      agentInput: {
        locale: this.locale,
      },
    }).valueChanges;

    this.loading$ = query$.pipe(map(({ loading }) => loading));
    this.items$ = query$.pipe(
      filter(({ data }) => Boolean(data)),
      map(({ data }) => [ data.mainCountryAgent ]),
    );
  }
}
